import { useEffect } from "react";
import { Link, Navigate, useLocation, useOutletContext, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { Block } from "./blocks";
import { DOCS_BASE } from "./paths";
import type { PlatformDocsContext } from "./platform-docs-layout";

export const PlatformDocsPage = () => {
  const { docs } = useOutletContext<PlatformDocsContext>();
  const { pageId } = useParams<{ pageId: string }>();
  const { hash } = useLocation();

  const pages = docs.groups.flatMap((g) => g.pages);
  const index = pages.findIndex((p) => p.id === pageId);
  const page = index >= 0 ? pages[index] : undefined;

  useEffect(() => {
    const target = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
    (target ?? document.getElementById("docs-scroll-top"))?.scrollIntoView();
  }, [pageId, hash]);

  if (!pageId) return <Navigate to={`${DOCS_BASE}/${pages[0].id}`} replace />;
  if (!page) return <Navigate to={`${DOCS_BASE}/${pages[0].id}`} replace />;

  const group = docs.groups.find((g) => g.pages.some((p) => p.id === page.id));
  const prev = pages[index - 1];
  const next = pages[index + 1];
  const isIntro = index === 0;

  return (
    <div className="mx-auto w-full max-w-5xl px-6 pb-24 pt-12 lg:px-10">
      <span id="docs-scroll-top" />

      {isIntro && (
        <div className="mb-10 rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 p-6 sm:p-8">
          <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-50 dark:bg-orange-500/10 text-orange-500">
            <BookOpen className="h-5 w-5" />
          </div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
            Developer documentation
          </p>
          <h1 className="mt-1.5 text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            {docs.title}
          </h1>
          <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">{docs.subtitle}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to={`${DOCS_BASE}/quickstart`}
              className="inline-flex items-center gap-1.5 rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-1.5 text-xs font-semibold text-orange-600 dark:text-orange-400 transition-colors hover:bg-orange-500/20"
            >
              Quickstart <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link
              to={`${DOCS_BASE}/scenario`}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-slate-800 px-4 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 transition-colors hover:bg-slate-50 dark:hover:bg-slate-900"
            >
              End-to-end scenario
            </Link>
          </div>
        </div>
      )}

      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-orange-500">{page.eyebrow ? `${group?.title} · ${page.eyebrow}` : group?.title}</p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
        {page.title}
      </h1>
      <p className="mt-3 text-[15px] leading-7 text-slate-500 dark:text-slate-400">{page.summary}</p>

      <div>
        {page.blocks.map((block, i) => (
          <Block key={i} block={block} />
        ))}
      </div>

      <nav className="mt-16 grid gap-4 border-t border-slate-200 dark:border-slate-800 pt-8 sm:grid-cols-2">
        {prev ? (
          <Link
            to={`${DOCS_BASE}/${prev.id}`}
            className="group rounded-2xl border border-slate-200 dark:border-slate-800 p-4 transition-colors hover:border-orange-500/40"
          >
            <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              <ArrowLeft className="h-3 w-3" /> Previous
            </span>
            <span className="mt-1 block text-sm font-semibold text-slate-900 dark:text-white group-hover:text-orange-500">
              {prev.title}
            </span>
          </Link>
        ) : (
          <span />
        )}
        {next && (
          <Link
            to={`${DOCS_BASE}/${next.id}`}
            className="group rounded-2xl border border-slate-200 dark:border-slate-800 p-4 text-right transition-colors hover:border-orange-500/40"
          >
            <span className="flex items-center justify-end gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Next <ArrowRight className="h-3 w-3" />
            </span>
            <span className="mt-1 block text-sm font-semibold text-slate-900 dark:text-white group-hover:text-orange-500">
              {next.title}
            </span>
          </Link>
        )}
      </nav>
    </div>
  );
};

export default PlatformDocsPage;
